import React, { useEffect, useMemo, useState } from "react";
import "./PolicyView.less";
import { Col, Collapse, Divider, Form, Row } from "antd";
import { Link, useHistory, useParams } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import { PolicyViewContext } from "../../context/PoliciesContext/PolicyViewContext";
import {
  fetchPolicyVersions,
  policiesActions,
  searchPolicy,
  updateCloseDatePolicy,
  updatePolicy,
} from "../../store/slice/policiesSlice";
import { useAppDispatch, useAppSelector } from "../../../..";
import PolicyTable from "../../features/PolicyTable/PolicyTable";
import PolicyParams from "../../features/PolicyParams/PolicyParams";
import { ReactComponent as VersionsHiddenIcon } from "../../../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../../../img/versions_icons/versionsShown_icon.svg";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { IChangingMode, IChangingRecordKeyNumber } from "../../../../lib/types/IViewMode";
import {
  checkIsLoading,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils";
import { ILoadByWithPromise } from "../../../../lib/types";
import { IPolicy } from "../../types/IPolicy";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { IPolicySearch } from "../../types/IPolicySearch";
import { DictionaryDto } from "../../../../lib/interfaces";

export const PolicyView = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { policyId } = useParams<{ policyId: string }>();

  const {
    policy,
    policyLoading,
    policyVersions,
    policyVersionsLoading,
    policiesTypesDic,
    policiesEntityObjectTypeDic,
  } = useAppSelector((state) => state.policies);

  const [policyParamsForm] = Form.useForm<IPolicySearch>();

  const [policyTypeSelect, setPolicyTypeSelect] = useState<DictionaryDto>();

  const onChangePolicyType = (id: number): void => {
    const findPolicyType = policiesTypesDic.find((dicEl) => dicEl.id === id);

    if (findPolicyType) {
      setPolicyTypeSelect(findPolicyType);
      return;
    }
    setPolicyTypeSelect(undefined);
  };

  // Загрузка политики по входу
  useEffect(() => {
    dispatch(searchPolicy(Number(policyId)));

    return () => {
      dispatch(policiesActions.cleanPolicy());
    };
  }, []);

  const [activePolicyVersion, setActivePolicyVersion] = useState<IPolicy>();

  // По дефолту activePolicyVersion === policy
  useEffect(() => {
    setActivePolicyVersion(policy);
  }, [policy]);

  console.log({ activePolicyVersion });

  useEffect(() => {
    policy?.typeId && onChangePolicyType(policy?.typeId);
  }, [policy, policiesTypesDic]);

  const loadPolicyVersions: ILoadByWithPromise = (id) => dispatch(fetchPolicyVersions(id));

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = () => {
    setHistoryModalVisible(true);
    setObjectId(policy?.id || null);
  };

  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState<boolean>(true);

  const toggleCollapse = () => {
    setIsCollapseOpen((prev) => !prev);
  };

  // Флаг редактируемой версии [индекс в массиве версий, mode]
  const [childChangingMode, setChildChangingMode] = useState<IChangingRecordKeyNumber>();

  const changeViewMode = (params?: IChangingRecordKeyNumber) => {
    setChildChangingMode(params);
  };

  const onClickVersionIcon = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    if (isVersionsOpen) {
      // dispatch(offeringActions.cleanOfferVersions());
      setChildChangingMode(undefined);
      setActivePolicyVersion(policy);
      setIsVersionsOpen(false);
    } else {
      policy?.id && loadPolicyVersions(policy.id);
      setIsVersionsOpen(true);
    }
  };

  // Добавление при создании версии
  const enrichedOfferVersions =
    useMemo(() => {
      if (!childChangingMode) return policyVersions;
      if (childChangingMode[1] === "add") {
        const [pos] = childChangingMode;
        const baseVersion = policyVersions[pos];
        const firstPart = policyVersions.slice(0, pos);
        const secondPart = policyVersions.slice(pos);
        return [...firstPart, baseVersion, ...secondPart];
      }
      return policyVersions;
    }, [policyVersions, childChangingMode]) || [];

  const sumbitUpdatePolicyVersion = async (values: IPolicySearch) => {
    const force = childChangingMode?.[1] === "edit";

    const assocTypeAlias = policyParamsForm.getFieldValue("assocTypeAlias");
    const actorTypeAlias = policyParamsForm.getFieldValue("actorTypeAlias");

    const editedPolicy: IPolicySearch<string> = {
      name: values?.name,
      typeId: values?.typeId,
      actionId: values?.actionId,
      dsc: values?.dsc,
      statusId: values?.statusId,
    };

    if (Number(values.actionId) === 5) {
      editedPolicy.info = {
        newOffering: policyParamsForm?.getFieldValue("newOffering"),
      };
    }

    if (assocTypeAlias) {
      editedPolicy.assocTypeId = policiesEntityObjectTypeDic?.find(
        (el) => el.alias === assocTypeAlias
      )?.id;
      editedPolicy.assocObjectId = policyParamsForm?.getFieldValue("assocObjectId");
    }
    if (actorTypeAlias) {
      editedPolicy.actorTypeId = policiesEntityObjectTypeDic?.find(
        (el) => el.alias === actorTypeAlias
      )?.id;
      editedPolicy.actorId = policyParamsForm?.getFieldValue("actorId");
    }

    dispatch(
      updatePolicy({
        body: editedPolicy,
        params: {
          policyId: Number(policyId),
          params: { force, fromDate: activePolicyVersion?.fromDate },
        },
      })
    ).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const sumbitUpdateCloseDatePolicyVersion = async (values: IPolicySearch) => {
    dispatch(
      updateCloseDatePolicy({
        policyId: Number(policyId),
        onDate: toHttpFormatEndOfDay(values?.toDate),
      })
    ).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const sumbitAddPolicyVersion = async (values: IPolicySearch) => {
    const force = childChangingMode?.[1] === "edit";

    const assocTypeAlias = policyParamsForm.getFieldValue("assocTypeAlias");
    const actorTypeAlias = policyParamsForm.getFieldValue("actorTypeAlias");
    const fromDate = toHttpFormatStartOfDay(policyParamsForm.getFieldValue("fromDate"));

    const addedPolicy: IPolicySearch<string> = {
      name: values?.name,
      typeId: values?.typeId,
      actionId: values?.actionId,
      dsc: values?.dsc,
      statusId: values?.statusId,

      fromDate,
    };

    if (Number(values.actionId) === 5) {
      addedPolicy.info = {
        newOffering: policyParamsForm?.getFieldValue("newOffering"),
      };
    }

    if (assocTypeAlias) {
      addedPolicy.assocTypeId = policiesEntityObjectTypeDic?.find(
        (el) => el.alias === assocTypeAlias
      )?.id;
      addedPolicy.assocObjectId = policyParamsForm?.getFieldValue("assocObjectId");
    }
    if (actorTypeAlias) {
      addedPolicy.actorTypeId = policiesEntityObjectTypeDic?.find(
        (el) => el.alias === actorTypeAlias
      )?.id;
      addedPolicy.actorId = policyParamsForm?.getFieldValue("actorId");
    }

    dispatch(
      updatePolicy({
        body: addedPolicy,
        params: {
          policyId: Number(policyId),
          params: { force, fromDate },
        },
      })
    ).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const onFinish = (fields) => {
    if (childChangingMode) {
      const mode: IChangingMode = childChangingMode[1];
      switch (mode) {
        case "edit":
          sumbitUpdatePolicyVersion(fields);
          break;
        case "add":
          sumbitAddPolicyVersion(fields);
          break;
        case "close":
          sumbitUpdateCloseDatePolicyVersion(fields);
          break;
      }
    }
  };

  return (
    <PolicyViewContext.Provider value={{}}>
      <div className="policy-view">
        <Row className="policy-view-title" align="middle">
          <Link to="/policies">
            <LeftOutlined style={{ fontSize: 16 }} />
          </Link>

          <h1>Просмотр политики</h1>

          <Divider className="policy-view-divider" style={{ marginBottom: 0 }} />
        </Row>

        <Form
          form={policyParamsForm}
          onFinish={onFinish}
          labelAlign="left"
          initialValues={{
            contextId: 1,
            statusId: 0,
          }}
          style={{ width: "100%" }}
        >
          <Collapse
            activeKey={isCollapseOpen ? ["1"] : []}
            bordered={false}
            onChange={toggleCollapse}
          >
            <Collapse.Panel
              key="1"
              header={
                <Row justify="space-between" className="policy-view-collapse-title">
                  <h3>{policy?.name ? `Политика "${policy?.name}"` : ""}</h3>
                  {isCollapseOpen && (
                    <Row align="middle" gutter={7}>
                      <Col>
                        {isVersionsOpen && <VersionsShownIcon onClick={onClickVersionIcon} />}
                        {!isVersionsOpen && <VersionsHiddenIcon onClick={onClickVersionIcon} />}
                      </Col>
                      <Col>
                        <DotsIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            showHistoryModal();
                          }}
                        />
                      </Col>
                    </Row>
                  )}
                </Row>
              }
            >
              <PolicyParams
                policy={policy}
                mode="view"
                isLoading={checkIsLoading(policyLoading)}
                onChangePolicyType={onChangePolicyType}
              />

              {isVersionsOpen &&
                enrichedOfferVersions.map((version, idx) => (
                  <div
                    key={idx}
                    style={
                      version.fromDate === activePolicyVersion?.fromDate
                        ? { border: "2px solid #4cf" }
                        : undefined
                    }
                    onClick={() => {
                      setActivePolicyVersion(version);
                    }}
                  >
                    {childChangingMode?.[0] !== idx && (
                      <PolicyParams
                        policy={version}
                        isChild
                        mode="view"
                        idx={idx}
                        isLatestVersion={idx === 0}
                        policyParamsForm={policyParamsForm}
                        isEditingDisabled={Boolean(childChangingMode)}
                        isLoading={checkIsLoading(policyVersionsLoading)}
                        changeViewMode={changeViewMode}
                        onChangePolicyType={onChangePolicyType}
                        setActivePolicyVersion={setActivePolicyVersion}
                      />
                    )}
                    {childChangingMode?.[0] === idx && (
                      <PolicyParams
                        policy={version}
                        policyParamsForm={policyParamsForm}
                        childChangingMode={childChangingMode}
                        mode={childChangingMode[1] === "close" ? "edit_close_date" : "edit"}
                        isLoading={checkIsLoading(policyVersionsLoading)}
                        onCancel={() => setChildChangingMode(undefined)}
                        onSubmit={policyParamsForm.submit}
                        onChangePolicyType={onChangePolicyType}
                      />
                    )}
                  </div>
                ))}
            </Collapse.Panel>
          </Collapse>

          <PolicyTable
            mode={
              childChangingMode?.[1] === "add" || childChangingMode?.[1] === "edit"
                ? "edit"
                : "view"
            }
            policyParamsForm={policyParamsForm}
            selectedTypeId={policyTypeSelect?.id}
            actorOfferingGroup={activePolicyVersion?.actorOfferingGroup}
            actorOffering={activePolicyVersion?.actorOffering}
            assocOffering={activePolicyVersion?.assocOffering}
            assocOfferingGroup={activePolicyVersion?.assocOfferingGroup}
          />
        </Form>
      </div>

      <HistoryAndPropertyModal
        objectTypeSysname="offering_policy"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
      />
    </PolicyViewContext.Provider>
  );
};
